import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Flag, User, Users, ArrowRight, ArrowLeft, Wand2, ClipboardPaste, Briefcase } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
// The redirect URL must be derived dynamically from window.location so preview
// and production environments both work without env-specific tweaks.
function startGoogleSignIn() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function GoogleGlyph({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  );
}


const ACCOUNT_TYPES = [
  {
    value: "candidate",
    icon: User,
    title: "I'm a Candidate",
    desc: "Running for office — manage your whole campaign.",
    accent: "red",
    testid: "register-account-type-candidate",
  },
  {
    value: "committee",
    icon: Users,
    title: "I'm a Party Committee",
    desc: "Running a local party committee — manage your messaging, events, and outreach.",
    accent: "navy",
    testid: "register-account-type-committee",
  },
  {
    value: "staffer",
    icon: Briefcase,
    title: "I'm Campaign Staff",
    desc: "Running operations on behalf of a candidate — volunteers, field, surrogate speeches, ops events.",
    accent: "red",
    testid: "register-account-type-staffer",
  },
];

export default function Register() {
  const { register, reload: reloadUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [accountType, setAccountType] = useState(null);
  const [importFirst, setImportFirst] = useState(false);
  const [inviteToken, setInviteToken] = useState("");      // raw token from ?invite=
  const [inviteEmail, setInviteEmail] = useState("");      // verified email from /invites/{token}/peek
  const [inviteInviter, setInviteInviter] = useState(""); // who sent it

  useEffect(() => {
    const t = (searchParams.get("type") || "").toLowerCase();
    if (t === "candidate" || t === "committee" || t === "staffer") {
      setAccountType(t);
    }
    // Invite flow — verify the token server-side so we don't trust the URL email blindly.
    const tok = (searchParams.get("invite") || "").trim();
    if (tok) {
      setInviteToken(tok);
      api.get(`/invites/${encodeURIComponent(tok)}/peek`)
        .then(({ data }) => {
          if (data?.email) {
            setInviteEmail(data.email);
            setEmail(data.email);
            setInviteInviter(data.invited_by_name || "");
          }
        })
        .catch(() => {
          // Bad / expired / used token — just fall through to a normal signup.
          setInviteToken("");
        });
    }
  }, [searchParams]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // Minimal race basics — collected post-account but pre-dashboard
  const [showBasics, setShowBasics] = useState(false);
  const [raceName, setRaceName] = useState(""); // candidate_name OR committee_name
  const [office, setOffice] = useState("");      // office_sought OR jurisdiction
  const [district, setDistrict] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  // Current legal versions the user must accept. Fetched once on mount;
  // sent back to the server on register so it can refuse a stale acceptance.
  const [legalVersions, setLegalVersions] = useState({ terms: "", privacy: "" });
  const [acceptedLegal, setAcceptedLegal] = useState(false);

  useEffect(() => {
    // Best-effort — if either fetch fails, the server enforces acceptance,
    // so a bad status here doesn't let the user through. Ignoring errors is
    // safe by design.
    Promise.all([
      api.get("/legal/terms").then(({ data }) => data.version).catch(() => ""),
      api.get("/legal/privacy").then(({ data }) => data.version).catch(() => ""),
    ]).then(([terms, privacy]) => setLegalVersions({ terms, privacy }));
  }, []);

  const isCommittee = accountType === "committee";
  const isStaffer = accountType === "staffer";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!acceptedLegal) {
      setError("Please accept the Terms of Service and Privacy Policy to create your account.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match. Please re-type your password.");
      return;
    }
    setLoading(true);
    const r = await register(name.trim(), email.trim(), password, legalVersions);
    if (!r.ok) {
      setLoading(false);
      setError(r.error);
      return;
    }
    // If the user arrived via invite link, lock the invite to this new account.
    // Best-effort: don't break the flow if the token has expired/conflicts.
    if (inviteToken) {
      try {
        await api.post(`/invites/${encodeURIComponent(inviteToken)}/accept`);
      } catch (err) {
        console.debug("invite accept failed (non-fatal)", err?.message || err);
      }
    }
    // Redeem an access code if one was entered. Quietly skip on failure
    // (we still let the user into the app on their 7-day trial).
    const code = accessCode.trim();
    if (code) {
      try {
        await api.post("/billing/redeem-code", { code });
      } catch (err) {
        // Non-fatal — the user can still retry from /billing. Log for debugging.
        console.debug("post-register code redemption failed", err?.message || err);
      }
    }
    // Save a stub campaign so account_type sticks (staffer detection, nav links,
    // prompts). Full race-info intake now happens AFTER activation.
    try {
      await api.put("/campaign", {
        account_type: accountType || "candidate",
        tone: "professional",
        top_issues: [],
        staffer_name: accountType === "staffer" ? name.trim() : "",
      });
    } catch (err) {
      console.debug("post-register stub-campaign save failed", err?.message || err);
    }

    // Refresh AuthContext so activation_status reflects any code redemption above
    // (access-code redeemers were just flipped server-side to active/comp).
    try { await reloadUser(); } catch { /* non-fatal */ }

    // Import-first path → land on the picker + import card; skip the race-basics microform.
    if (importFirst) {
      setLoading(false);
      navigate("/intake");
      return;
    }

    // Explicit card step: ask the backend if this user needs a card, and if so
    // hard-redirect them to Stripe RIGHT NOW so it's the literal next screen.
    // The endpoint short-circuits (checkout_url=null) for owner/comp/active
    // users, which preserves invite/comp/access-code/owner bypasses.
    try {
      const { data } = await api.post("/billing/activation-checkout", {
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        // Match the one-shot flag ActivationGate uses so it never double-fires.
        try { sessionStorage.setItem("activation_redirect_started", "1"); } catch { /* noop */ }
        window.location.href = data.checkout_url;
        return;
      }
    } catch (err) {
      // Non-fatal — ActivationGate is still there as a backstop.
      console.debug("register→activation-checkout failed", err?.message || err);
    }

    // Bypass path (owner / comp / access-code / already-active) → straight to intake.
    setLoading(false);
    navigate("/intake");
  };

  const onSaveBasics = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = {
        account_type: accountType || "candidate",
        tone: "professional",
        top_issues: [],
        candidate_name: isCommittee ? "" : raceName.trim(),
        committee_name: isCommittee ? raceName.trim() : "",
        staffer_name: isStaffer ? name.trim() : "",
        office_sought: isCommittee ? "" : office.trim(),
        jurisdiction: isCommittee ? office.trim() : "",
        district: district.trim(),
        bio: "",
        own_weaknesses: "",
        opponent_name: "",
        opponent_platform: "",
        opponent_vulnerabilities: "",
        party: "",
        key_events: "",
        opposition_messaging: "",
        election_date: "",
      };
      await api.put("/campaign", payload);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Could not save");
    } finally {
      setLoading(false);
    }
  };

  const skipBasics = async () => {
    // Save a stub campaign so account_type sticks even if the user skipped the
    // basics microform — required for staffer detection / nav links / prompts.
    try {
      await api.put("/campaign", {
        account_type: accountType || "candidate",
        tone: "professional",
        top_issues: [],
        staffer_name: accountType === "staffer" ? name.trim() : "",
      });
    } catch (err) {
      // Non-fatal — user can complete intake later. Log so we can see it in dev tools.
      console.debug("post-register stub-campaign save failed", err?.message || err);
    }
    navigate("/dashboard");
  };

  const ready = accountType || importFirst;

  return (
    <div className="min-h-screen bg-flag-ivory flex flex-col">
      <div className="stripe-accent" />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-2xl">
          <Link
            to="/"
            className="flex items-center gap-2 justify-center font-display font-black text-2xl text-flag-navy mb-6"
            data-testid="register-logo"
          >
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-flag-red text-white">
              <Flag className="h-4 w-4" />
            </span>
            Electable
          </Link>

          {/* ===== Step 1 — Account type picker ===== */}
          {!ready && (
            <div data-testid="register-step-type">
              <div className="text-center mb-6">
                <p className="text-xs font-bold uppercase tracking-wider text-flag-red">Step 1 of 2</p>
                <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">
                  Who's signing up?
                </h1>
                <p className="text-slate-600 mt-2">
                  Pick the account type that fits you. We'll tailor the intake and dashboard accordingly.
                </p>
              </div>

              {/* Google Sign-in — fastest path. New Google users still go
                  through account-type pick (via /intake), legal acceptance,
                  and the card-required trial gate, identical to email flow. */}
              <div className="max-w-md mx-auto mb-6" data-testid="register-google-block">
                <button
                  type="button"
                  onClick={startGoogleSignIn}
                  data-testid="register-google-btn"
                  className="w-full h-12 inline-flex items-center justify-center gap-3 rounded-md border-2 border-slate-300 bg-white hover:bg-slate-50 hover:border-slate-400 text-flag-navy font-bold text-base shadow-sm transition-colors"
                >
                  <GoogleGlyph className="h-5 w-5" />
                  Continue with Google
                </button>
                <div className="mt-4 flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-slate-500" data-testid="register-google-divider">
                  <span className="flex-1 h-px bg-slate-200" />
                  or sign up with email
                  <span className="flex-1 h-px bg-slate-200" />
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                {ACCOUNT_TYPES.map((a) => {
                  const Icon = a.icon;
                  const hoverBorder = a.accent === "red" ? "hover:border-flag-red" : "hover:border-flag-navy";
                  const iconBg = a.accent === "red" ? "bg-flag-red/10 text-flag-red" : "bg-flag-navy/10 text-flag-navy";
                  const cta = a.accent === "red" ? "text-flag-red" : "text-flag-navy";
                  return (
                    <button
                      key={a.value}
                      type="button"
                      onClick={() => setAccountType(a.value)}
                      data-testid={a.testid}
                      className={`text-left bg-white border-2 border-slate-200 ${hoverBorder} rounded-xl p-6 sm:p-7 shadow-sm transition-colors group`}
                    >
                      <div className={`h-12 w-12 rounded-lg ${iconBg} flex items-center justify-center`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <h2 className="mt-4 font-display text-xl font-bold text-flag-navy">{a.title}</h2>
                      <p className="mt-2 text-slate-600 leading-relaxed">{a.desc}</p>
                      <div className={`mt-4 inline-flex items-center gap-1 font-semibold ${cta}`}>
                        Continue <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </button>
                  );
                })}
              </div>

              <p className="mt-6 text-sm text-slate-600 text-center">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="font-semibold text-flag-navy hover:underline"
                  data-testid="register-login-link"
                >
                  Sign in
                </Link>
              </p>

              {/* Import-from-another-AI path */}
              <div
                className="mt-6 bg-white border-2 border-flag-red rounded-xl p-5 flex flex-wrap items-center justify-between gap-3 shadow-sm"
                data-testid="register-import-card"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-flag-red text-white flex-shrink-0">
                    <Wand2 className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-display font-bold text-flag-navy">Already set this up with another AI?</div>
                      <span
                        data-testid="register-import-recommended-badge"
                        className="inline-flex items-center text-[10px] font-bold uppercase tracking-[0.16em] bg-flag-red text-white px-2 py-0.5 rounded-full"
                      >
                        Highly recommended
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 mt-1 leading-relaxed">
                      Bring the work you&rsquo;ve done in ChatGPT, Gemini, or another AI. You&rsquo;ll create the account in 30 seconds, then paste it in — Electable will populate your profile so every post, speech, and graphic is grounded in your race.
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={() => setImportFirst(true)}
                  data-testid="register-import-btn"
                  className="h-10 px-4 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
                >
                  <ClipboardPaste className="h-4 w-4 mr-1.5" /> Import after signup
                </Button>
              </div>
            </div>
          )}
          {ready && !showBasics && (
            <div className="w-full max-w-md mx-auto" data-testid="register-step-details">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8">
                <button
                  type="button"
                  onClick={() => {
                    setAccountType(null);
                    setImportFirst(false);
                    setError("");
                  }}
                  data-testid="register-change-type-btn"
                  className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-flag-navy"
                >
                  <ArrowLeft className="h-4 w-4" /> Change account type
                </button>

                <div className="mt-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-flag-red">Step 2 of 2</p>
                  <h1 className="font-display text-2xl font-bold text-flag-navy mt-1">Create your account</h1>
                  <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full" data-testid="register-selected-type-badge">
                    {(() => {
                      if (importFirst) return <><Wand2 className="h-3 w-3" /> Importing from another AI</>;
                      if (isCommittee) return <><Users className="h-3 w-3" /> Party Committee</>;
                      if (isStaffer) return <><Briefcase className="h-3 w-3" /> Campaign Staff</>;
                      return <><User className="h-3 w-3" /> Candidate</>;
                    })()}
                  </div>
                </div>

                <form onSubmit={onSubmit} className="mt-6 space-y-4">
                  {inviteEmail && (
                    <div
                      className="rounded-lg border border-flag-red/30 bg-flag-red/[0.04] px-4 py-3 text-sm text-flag-navy"
                      data-testid="register-invite-banner"
                    >
                      <span className="font-bold">You're using an invite.</span>{" "}
                      {inviteInviter ? `${inviteInviter} invited` : "You've been invited"} you to join Electable as{" "}
                      <span className="font-mono text-flag-red">{inviteEmail}</span>. We've filled in your email — just set a name and password.
                    </div>
                  )}
                  <div>
                    <Label htmlFor="name" className="text-sm font-semibold text-slate-800">
                      {accountType === "committee" ? "Your name" : "Full name"}
                    </Label>
                    <Input
                      id="name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="register-name-input"
                      className="mt-1 h-12 text-base"
                      placeholder={accountType === "committee" ? "Committee chair name" : "Jane Candidate"}
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm font-semibold text-slate-800">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="register-email-input"
                      className={`mt-1 h-12 text-base ${inviteEmail ? "bg-slate-100 text-slate-700 cursor-not-allowed" : ""}`}
                      placeholder="you@campaign.com"
                      readOnly={!!inviteEmail}
                    />
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-sm font-semibold text-slate-800">Password</Label>
                    <PasswordInput
                      id="password"
                      autoComplete="new-password"
                      required
                      minLength={6}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="register-password-input"
                      className="mt-1 h-12 text-base"
                      placeholder="At least 6 characters"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm-password" className="text-sm font-semibold text-slate-800">Confirm password</Label>
                    <PasswordInput
                      id="confirm-password"
                      autoComplete="new-password"
                      required
                      minLength={6}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      data-testid="register-confirm-password-input"
                      className="mt-1 h-12 text-base"
                      placeholder="Re-type your password"
                    />
                    {confirmPassword && password !== confirmPassword && (
                      <p className="mt-1 text-xs font-semibold text-flag-red" data-testid="register-confirm-password-mismatch">
                        Passwords don&rsquo;t match yet.
                      </p>
                    )}
                  </div>
                  {error && (
                    <div className="text-sm text-flag-red bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="register-error">
                      {error}
                    </div>
                  )}
                  <details className="border border-slate-200 rounded-md">
                    <summary className="cursor-pointer select-none px-3 py-2 text-sm font-semibold text-slate-700 hover:text-flag-navy" data-testid="register-access-code-toggle">
                      Have an access code? <span className="font-normal text-slate-500">(optional)</span>
                    </summary>
                    <div className="px-3 pb-3 pt-1">
                      <Input
                        value={accessCode}
                        onChange={(e) => setAccessCode(e.target.value)}
                        data-testid="register-access-code-input"
                        className="h-10 uppercase tracking-wider"
                        placeholder="Enter code"
                      />
                      <p className="mt-1.5 text-xs text-slate-500">
                        Skip this if you don't have one — your 7-day free trial starts automatically.
                      </p>
                    </div>
                  </details>
                  <label className="flex items-start gap-2.5 text-sm text-slate-800 cursor-pointer select-none" data-testid="register-legal-label">
                    <input
                      type="checkbox"
                      checked={acceptedLegal}
                      onChange={(e) => setAcceptedLegal(e.target.checked)}
                      data-testid="register-legal-checkbox"
                      className="mt-1 h-4 w-4 rounded border-slate-400 text-flag-red focus:ring-flag-red"
                    />
                    <span>
                      I have read and agree to the{" "}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-flag-red font-semibold hover:underline" data-testid="register-legal-terms-link">
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-flag-red font-semibold hover:underline" data-testid="register-legal-privacy-link">
                        Privacy Policy
                      </a>.
                    </span>
                  </label>
                  <Button
                    type="submit"
                    disabled={loading || !password || password !== confirmPassword || !acceptedLegal}
                    data-testid="register-submit-btn"
                    className="w-full h-12 bg-flag-red hover:bg-flag-red-hover text-white font-bold text-base"
                  >
                    {loading ? "Creating…" : "Start free 7-day trial"}
                  </Button>
                  <p className="text-center text-xs text-slate-500">
                    $0 due today. On the next screen you'll add a card — it won't be charged until your trial ends in 7 days. Cancel anytime before then and you won't be charged at all.
                  </p>
                </form>
                <p className="mt-6 text-sm text-slate-600 text-center">
                  Already have an account?{" "}
                  <Link to="/login" className="font-semibold text-flag-navy hover:underline" data-testid="register-login-link">
                    Sign in
                  </Link>
                </p>
              </div>
            </div>
          )}

          {/* ===== Step 3 — Minimal race basics (just enough to get into the app) ===== */}
          {showBasics && (
            <div className="w-full max-w-md mx-auto" data-testid="register-step-basics">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8">
                <p className="text-xs font-bold uppercase tracking-wider text-flag-red">Almost there · Step 3 of 3</p>
                <h1 className="font-display text-2xl font-bold text-flag-navy mt-1">Quick race basics</h1>
                <p className="text-sm text-slate-600 mt-2">
                  Just three quick fields so the tools have something to work with. You can add everything else from the dashboard later — and the <strong>Import from another AI</strong> option is one click away.
                </p>
                <form onSubmit={onSaveBasics} className="mt-5 space-y-4">
                  <div>
                    <Label htmlFor="race-name" className="text-sm font-semibold text-slate-800">
                      {isStaffer ? "Candidate name (the candidate you work for)" : isCommittee ? "Committee name" : "Candidate name"}
                    </Label>
                    <Input
                      id="race-name"
                      required
                      value={raceName}
                      onChange={(e) => setRaceName(e.target.value)}
                      data-testid="register-basics-name"
                      className="mt-1 h-11 text-base"
                      placeholder={isStaffer ? "The candidate you work for" : isCommittee ? "Ontario County Republican Committee" : "Jane Candidate"}
                    />
                  </div>
                  <div>
                    <Label htmlFor="race-office" className="text-sm font-semibold text-slate-800">
                      {isCommittee ? "Jurisdiction" : "Office sought"}
                    </Label>
                    <Input
                      id="race-office"
                      required
                      value={office}
                      onChange={(e) => setOffice(e.target.value)}
                      data-testid="register-basics-office"
                      className="mt-1 h-11 text-base"
                      placeholder={isCommittee ? "Ontario County, NY" : "Town Supervisor"}
                    />
                  </div>
                  <div>
                    <Label htmlFor="race-district" className="text-sm font-semibold text-slate-800">District / community</Label>
                    <Input
                      id="race-district"
                      required
                      value={district}
                      onChange={(e) => setDistrict(e.target.value)}
                      data-testid="register-basics-district"
                      className="mt-1 h-11 text-base"
                      placeholder={isCommittee ? "All of Ontario County, NY" : "Town of Bristol, Ontario County"}
                    />
                  </div>
                  {error && (
                    <div className="text-sm text-flag-red bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="register-basics-error">
                      {error}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-3 pt-1">
                    <Button
                      type="submit"
                      disabled={loading}
                      data-testid="register-basics-submit-btn"
                      className="h-11 px-5 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
                    >
                      {loading ? "Saving…" : "Save & enter Electable"} <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <button
                      type="button"
                      onClick={skipBasics}
                      data-testid="register-basics-skip-btn"
                      className="text-sm font-semibold text-slate-600 hover:text-flag-navy"
                    >
                      I&rsquo;ll do it later
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
